/**
 * Umzüge.
 *
 * Bisher war die Verlagerung von Diensten im Panel praktisch unauffindbar: Sie
 * steckte in einem Knopf über der Serverliste, und wenn sie lief, war sie
 * nirgends zu sehen. Genau das ist der Grund für diese Seite. Ein Umzug ist im
 * Betrieb kein Nebenschauplatz, sondern ein Vorgang, den man über Stunden
 * beobachtet – mit Fortschritt, Rate und der Frage, wann es endlich durch ist.
 *
 * Die Seite beantwortet in dieser Reihenfolge:
 *
 *   Was läuft gerade? ──► Was wartet? ──► Was sollte ich anstoßen?
 *
 * Und sie ist zugleich der Ort, an dem Umzüge *beginnen*: „Server räumen“ ist
 * der häufigste Fall im Betrieb ("diese Kiste muss raus") und stand bisher
 * nirgends.
 */
import { useMemo, useState } from 'react';
import type { ContractId, MigrationJob, Product, Server, ServerId, TransportJob, VpsId, World } from '@hosting-sim/core';
import {
  consolidationSuggestions,
  estimateMigration,
  fits,
  gbPerTick,
  planEvacuation,
  requiredRole,
  runningMigrations,
  runningTransports,
  serverCapacity,
} from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  Column,
  DataTable,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Mono,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  Tabs,
} from '../components/ui';
import { ConsolidateButton, RetirementNotice } from '../components/Consolidation';
import { useGame } from '../store/game';
import { api } from '../lib/api';
import { bytes, duration, mbps, money, num, pct } from '../lib/format';

const PHASE_LABEL: Record<MigrationJob['phase'], string> = {
  queued: 'wartet',
  disk: 'Plattendaten',
  memory: 'Speicherabgleich',
  cutover: 'schaltet um',
  done: 'fertig',
  failed: 'abgebrochen',
};

const PHASE_TONE: Record<MigrationJob['phase'], 'muted' | 'info' | 'warning' | 'success' | 'danger'> = {
  queued: 'muted',
  disk: 'info',
  memory: 'warning',
  cutover: 'warning',
  done: 'success',
  failed: 'danger',
};

/*
 * Hardwaretransporte.
 *
 * Sie stehen auf derselben Seite wie die Live-Migrationen, weil beide dieselbe
 * Frage beantworten – wo läuft welcher Dienst? –, und sie sind trotzdem
 * getrennt aufgeführt: Ein Umzug über die Leitung dauert Stunden und stört
 * niemanden, ein Umzug auf dem Lastwagen dauert Tage und schaltet das Gerät ab.
 * In einer gemeinsamen Tabelle wäre dieser Unterschied verschwunden.
 */
const TRANSPORT_LABEL: Record<TransportJob['phase'], string> = {
  packing: 'wird verpackt',
  transit: 'unterwegs',
  install: 'wird eingebaut',
  done: 'angekommen',
  failed: 'abgebrochen',
};

const TRANSPORT_TONE: Record<TransportJob['phase'], 'muted' | 'info' | 'warning' | 'success' | 'danger'> = {
  packing: 'muted',
  transit: 'info',
  install: 'warning',
  done: 'success',
  failed: 'danger',
};

/** Wie weit ein Auftrag ist, 0..1. */
function progressOf(job: MigrationJob): number {
  const total = job.diskGbTotal + job.ramGbTotal;
  if (total <= 0) return job.phase === 'done' ? 1 : 0;
  const sent = job.diskGbSent + (job.ramGbTotal - job.ramGbOutstanding);
  return Math.min(1, Math.max(0, sent / total));
}

/** Was noch zu übertragen ist, in GB. */
function remainingGb(job: MigrationJob): number {
  return Math.max(0, job.diskGbTotal - job.diskGbSent) + job.ramGbOutstanding;
}

export function Migrations() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [tab, setTab] = useState('laufend');
  const [evacuating, setEvacuating] = useState<ServerId | null>(null);
  const [moving, setMoving] = useState(false);

  const jobs = useMemo(() => (world ? Object.values(world.migrations) : []), [world]);
  const transports = useMemo(() => (world ? runningTransports(world) : []), [world]);
  const running = useMemo(() => (world ? runningMigrations(world) : []), [world]);
  const finished = useMemo(
    () => jobs.filter((j) => j.phase === 'done' || j.phase === 'failed').sort((a, b) => b.startedAtTick - a.startedAtTick),
    [jobs],
  );
  const suggestions = useMemo(() => (world ? consolidationSuggestions(world).slice(0, 3) : []), [world]);

  if (!world) return null;

  const queued = running.filter((j) => j.phase === 'queued');
  const transferring = running.filter((j) => j.phase !== 'queued');
  const openGb = running.reduce((sum, job) => sum + remainingGb(job), 0);
  const totalRate = transferring.reduce((sum, job) => sum + job.rateMbps, 0);

  const label = (job: MigrationJob): string => {
    if (job.subject.kind === 'vps') return world.vpsInstances[job.subject.id]?.name ?? 'Instanz';
    const contract = world.contracts[job.subject.id];
    const product = contract ? world.products[contract.productId] : undefined;
    const customer = contract ? world.customers[contract.customerId] : undefined;
    return `${product?.name ?? 'Vertrag'} – ${customer?.company ?? customer?.name ?? 'Kunde'}`;
  };

  const columns: Column<MigrationJob>[] = [
    {
      key: 'subject',
      header: 'Dienst',
      sortValue: (j) => label(j),
      render: (j) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{label(j)}</p>
          <p className="text-2xs text-panel-500 dark:text-panel-400">
            {j.subject.kind === 'vps' ? 'Live-Migration' : 'Vertragsumzug'}
            {j.reason === 'consolidation' && ' · Zusammenlegung'}
            {j.reason === 'evacuation' && ' · Räumung'}
          </p>
        </div>
      ),
    },
    {
      key: 'route',
      header: 'Von → nach',
      hideOnMobile: true,
      render: (j) => (
        <span className="text-xs">
          {world.servers[j.sourceServerId]?.name ?? '?'} → <strong>{world.servers[j.targetServerId]?.name ?? '?'}</strong>
        </span>
      ),
    },
    {
      key: 'phase',
      header: 'Phase',
      sortValue: (j) => j.phase,
      render: (j) => (
        <div className="min-w-0">
          <Badge tone={PHASE_TONE[j.phase]}>{PHASE_LABEL[j.phase]}</Badge>
          {j.passes > 1 && j.phase === 'memory' && (
            <span className="ml-1.5 text-2xs text-panel-500 dark:text-panel-400">{j.passes}. Durchgang</span>
          )}
        </div>
      ),
    },
    {
      key: 'progress',
      header: 'Fortschritt',
      render: (j) => (
        <div className="w-32">
          <Meter
            value={progressOf(j)}
            max={1}
            label={
              <>
                <span>{bytes(remainingGb(j))} offen</span>
                <span className="tabular">{pct(progressOf(j), 0)}</span>
              </>
            }
          />
        </div>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      align: 'right',
      hideOnMobile: true,
      sortValue: (j) => j.rateMbps,
      render: (j) => <span className="tabular text-xs">{j.rateMbps > 0 ? mbps(j.rateMbps) : '–'}</span>,
    },
    {
      key: 'eta',
      header: 'Restdauer',
      align: 'right',
      render: (j) => {
        const perTick = gbPerTick(j.rateMbps);
        if (j.phase === 'queued') return <span className="text-xs text-panel-500">in der Schlange</span>;
        if (perTick <= 0) return <span className="text-xs text-panel-500">–</span>;
        return <span className="tabular text-xs">{duration(remainingGb(j) / perTick)}</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (j) => (
        <Button size="sm" variant="ghost" onClick={() => void run({ type: 'migration/cancel', migrationId: j.id })}>
          Abbrechen
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Umzüge"
        description="Live-Migrationen und Vertragsumzüge: was läuft, was wartet, was sich lohnen würde."
        actions={
          <>
            <Button onClick={() => setMoving(true)}>Dienste verlegen</Button>
            <ConsolidateButton />
            <Button variant="primary" onClick={() => setEvacuating(Object.values(world.servers)[0]?.id ?? null)}>
              Server räumen
            </Button>
          </>
        }
      />

      <StatGrid cols={4}>
        <Stat
          label="Laufende Übertragungen"
          value={num(transferring.length)}
          hint={queued.length > 0 ? `${queued.length} warten auf eine Spur` : 'keine Warteschlange'}
        />
        <Stat label="Noch zu übertragen" value={bytes(openGb)} hint="Platten- und Speicherdaten zusammen" />
        <Stat label="Gesamtrate" value={totalRate > 0 ? mbps(totalRate) : '–'} hint="alle Übertragungen zusammen" />
        <Stat
          label="Umschaltpausen heute"
          value={num(finished.filter((j) => j.freezeSeconds >= 60).length)}
          tone={finished.some((j) => j.freezeSeconds >= 60) ? 'warning' : 'neutral'}
          hint="Umzüge, die der Kunde bemerkt hat"
        />
      </StatGrid>

      <div className="mt-4 space-y-3">
        <RetirementNotice />

        {suggestions.length > 0 && (
          <Notice tone="info" title="Das würde sich lohnen">
            <ul className="space-y-1 text-xs">
              {suggestions.map((s) => (
                <li key={s.targetServerId}>
                  {s.sourceServerIds.length} Server auf <strong>{world.servers[s.targetServerId]?.name}</strong> zusammenlegen —
                  spart {money(s.plan.monthlySaving)}/Monat Strom, bringt {money(s.plan.proceeds)} Erlös,{' '}
                  {bytes(s.plan.transferGb)} über {mbps(s.plan.rateMbps)} zu übertragen ({duration(s.plan.durationTicks)}).
                </li>
              ))}
            </ul>
          </Notice>
        )}

        {transferring.some((j) => j.passes >= 4) && (
          <Notice tone="warning" title="Ein Speicherabgleich kommt nicht hinterher">
            Die Instanz beschreibt ihren Arbeitsspeicher schneller, als die Leitung ihn überträgt. Nach einigen Durchgängen wird
            das Umschalten erzwungen – mit einer Pause, die der Kunde bemerkt. Umzüge großer Instanzen gehören in die Nacht: Bei
            niedriger Last konvergiert der Abgleich von selbst.
          </Notice>
        )}
      </div>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'laufend', label: 'Laufend', badge: <Badge tone="muted">{running.length}</Badge> },
            { id: 'transport', label: 'Hardware unterwegs', badge: <Badge tone="muted">{transports.length}</Badge> },
            { id: 'erledigt', label: 'Abgeschlossen', badge: <Badge tone="muted">{finished.length}</Badge> },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div>

      {tab === 'laufend' && (
        <Card className="mt-4" padded={false}>
          {running.length === 0 ? (
            <EmptyState
              title="Es zieht gerade nichts um"
              description="Ein Umzug ist eine Datenübertragung: Er dauert so lange, wie die Anbindung der beteiligten Knoten hergibt. Über „Server räumen“ lässt sich ein einzelner Knoten vollständig leeren – der übliche Weg, bevor er verkauft oder aufgerüstet wird."
              action={
                <Button variant="primary" onClick={() => setEvacuating(Object.values(world.servers)[0]?.id ?? null)}>
                  Server räumen
                </Button>
              }
            />
          ) : (
            <DataTable columns={columns} rows={running} rowKey={(j) => j.id} empty="Nichts unterwegs." />
          )}
        </Card>
      )}

      {tab === 'transport' && (
        <Card className="mt-4" padded={false}>
          {transports.length === 0 ? (
            <EmptyState
              title="Es fährt gerade nichts"
              description="Geräte lassen sich zwischen Standorten umsetzen – ausbauen, fahren, einbauen. Ein Server muss dafür leer sein: Was auf dem Lastwagen steht, liefert nichts aus. Der Umzug eines ganzen Standorts beginnt unter „Rechenzentren“ beim jeweiligen Vertrag."
            />
          ) : (
            <DataTable
              columns={[
                {
                  key: 'device',
                  header: 'Gerät',
                  render: (t: TransportJob) => (
                    <div className="min-w-0">
                      <p className="truncate font-medium">{t.deviceName}</p>
                      <p className="text-2xs text-panel-500 dark:text-panel-400">
                        {world.datacenters[t.fromDatacenterId]?.city ?? 'Altstandort'} → {world.datacenters[t.toDatacenterId]?.city ?? 'Zielstandort'} ·{' '}
                        {num(t.distanceKm)} km
                      </p>
                    </div>
                  ),
                },
                {
                  key: 'phase',
                  header: 'Stand',
                  render: (t: TransportJob) => (
                    <div className="inline-flex items-center gap-2">
                      <Badge tone={TRANSPORT_TONE[t.phase]}>{TRANSPORT_LABEL[t.phase]}</Badge>
                      <span className="text-2xs text-panel-500">{t.note}</span>
                    </div>
                  ),
                },
                {
                  key: 'left',
                  header: 'Noch',
                  align: 'right',
                  render: (t: TransportJob) => <span className="tabular text-xs">{duration(t.ticksRemaining)}</span>,
                },
                {
                  key: 'cost',
                  header: 'Spedition',
                  align: 'right',
                  hideOnMobile: true,
                  render: (t: TransportJob) => <span className="tabular text-xs">{money(t.cost)}</span>,
                },
                {
                  key: 'risk',
                  header: 'Schadensrisiko',
                  align: 'right',
                  hideOnMobile: true,
                  render: (t: TransportJob) => (
                    <span className={`tabular text-xs ${t.damageRisk > 0.08 ? 'text-amber-600 dark:text-amber-400' : 'text-panel-500'}`}>
                      {pct(t.damageRisk, 1)}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (t: TransportJob) =>
                    t.phase === 'packing' ? (
                      <Button size="sm" variant="ghost" onClick={() => void run({ type: 'dc/cancelTransport', transportId: t.id })}>
                        Abbrechen
                      </Button>
                    ) : (
                      <span className="text-2xs text-panel-500">kein Zurück</span>
                    ),
                },
              ]}
              rows={transports}
              rowKey={(t) => t.id}
              empty="Nichts unterwegs."
            />
          )}
        </Card>
      )}

      {tab === 'erledigt' && (
        <Card className="mt-4" padded={false}>
          <DataTable
            columns={[
              { key: 'subject', header: 'Dienst', render: (j: MigrationJob) => <span className="text-xs">{label(j)}</span> },
              {
                key: 'route',
                header: 'Nach',
                render: (j: MigrationJob) => <span className="text-xs">{world.servers[j.targetServerId]?.name ?? '–'}</span>,
              },
              {
                key: 'result',
                header: 'Ergebnis',
                render: (j: MigrationJob) => <Badge tone={PHASE_TONE[j.phase]}>{PHASE_LABEL[j.phase]}</Badge>,
              },
              {
                key: 'freeze',
                header: 'Umschaltpause',
                align: 'right',
                render: (j: MigrationJob) =>
                  j.phase === 'done' ? (
                    <span className={`tabular text-xs ${j.freezeSeconds >= 60 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                      {j.freezeSeconds >= 60 ? `${Math.round(j.freezeSeconds / 60)} min` : `${j.freezeSeconds} s`}
                    </span>
                  ) : (
                    <span className="text-xs text-panel-500">–</span>
                  ),
              },
              { key: 'note', header: 'Anmerkung', hideOnMobile: true, render: (j: MigrationJob) => <span className="text-2xs text-panel-500">{j.note}</span> },
            ]}
            rows={finished}
            rowKey={(j) => j.id}
            dense
            empty="Noch nichts abgeschlossen."
          />
        </Card>
      )}

      {evacuating && <EvacuationDialog serverId={evacuating} onClose={() => setEvacuating(null)} />}
      {moving && <MoveServicesDialog onClose={() => setMoving(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Server räumen
// ---------------------------------------------------------------------------

/**
 * Knopf mit Dialog, überall dort einsetzbar, wo ein einzelner Server im
 * Mittelpunkt steht.
 *
 * Der Grund für den Export: „Diese Kiste muss raus“ entsteht in der
 * Serveransicht, nicht in einer Umzugsübersicht. Wer erst dorthin navigieren
 * und den Server erneut auswählen muss, tut es nicht.
 */
export function EvacuateServerButton({ serverId, label = 'Räumen' }: { serverId: ServerId; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="primary" onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open && <EvacuationDialog serverId={serverId} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Der Dialog beantwortet vor dem Klick, was das Räumen kostet: wie lange es
 * dauert, wie viele Kunden es merken und was zurückbleibt. Ein Knoten, der
 * nicht vollständig leer wird, lässt sich nicht abgeben – und genau das ist die
 * Information, die man vorher braucht, nicht hinterher.
 */
function EvacuationDialog({ serverId, onClose }: { serverId: ServerId; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [selected, setSelected] = useState<ServerId>(serverId);
  const [retire, setRetire] = useState(true);
  const [busy, setBusy] = useState(false);

  const servers = useMemo(() => (world ? Object.values(world.servers) : []), [world]);
  const plan = useMemo(() => (world ? planEvacuation(world, selected) : null), [world, selected]);

  if (!world || !plan) return null;
  const server = world.servers[selected];

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Server räumen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!!plan.error || plan.steps.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                const result = await run({ type: 'server/evacuate', serverId: selected, retireAfterwards: retire });
                if (result.ok) onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            Räumung starten
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="Welcher Server soll geräumt werden?"
          value={selected}
          onChange={(e) => setSelected(e.target.value as ServerId)}
          hint="Jede Last sucht sich selbst den passendsten freien Knoten – ein Ziel muss nicht vorgegeben werden."
        >
          {servers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.vpsIds.length + s.contractIds.length} Dienste, {world.datacenters[s.datacenterId]?.city}
            </option>
          ))}
        </Select>

        {plan.error ? (
          <Notice tone="warning" title="So geht es nicht">
            {plan.error}
          </Notice>
        ) : plan.steps.length === 0 ? (
          <Notice tone="info" title="Nichts zu tun">
            Auf {server?.name} liegt nichts, was sich verlegen ließe. Er lässt sich direkt abgeben.
          </Notice>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 rounded-lg border divider p-3 text-xs sm:grid-cols-4">
              <div>
                <p className="text-panel-500 dark:text-panel-400">Verlagerungen</p>
                <p className="font-medium">{num(plan.steps.length)}</p>
              </div>
              <div>
                <p className="text-panel-500 dark:text-panel-400">Zu übertragen</p>
                <p className="font-medium">{bytes(plan.steps.reduce((sum, s) => sum + s.diskGb + s.ramGb, 0))}</p>
              </div>
              <div>
                <p className="text-panel-500 dark:text-panel-400">Dauer</p>
                <p className="font-medium">{duration(plan.totalTicks)}</p>
              </div>
              <div>
                <p className="text-panel-500 dark:text-panel-400">Kunden merken es</p>
                <p className={`font-medium ${plan.disruptive > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{num(plan.disruptive)}</p>
              </div>
            </div>

            <Notice tone="info" title="Warum das so lange dauert">
              Alle Übertragungen laufen über die Anbindung von {server?.name} und teilen sie sich. Sie gleichzeitig zu starten
              macht sie nicht schneller – deshalb ist die Dauer die Summe und nicht das Maximum. Eine schnellere
              Netzwerkkarte oder ein 10G-Port am Switch verkürzt sie unmittelbar.
            </Notice>

            <div>
              <p className="mb-2 text-xs font-medium">Wohin was geht</p>
              <ul className="space-y-1">
                {plan.steps.map((step) => (
                  <li key={`${step.kind}-${step.id}`} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
                    <span className="min-w-0 flex-1 truncate">{step.label}</span>
                    <span className="text-panel-500 dark:text-panel-400">→ {step.targetName}</span>
                    <span className="tabular text-panel-500 dark:text-panel-400">{bytes(step.diskGb + step.ramGb)}</span>
                    <span className="tabular text-panel-500 dark:text-panel-400">{duration(step.ticks)}</span>
                    {step.seamless ? (
                      <Badge tone="success">ohne Unterbrechung</Badge>
                    ) : step.freezeSeconds >= 60 ? (
                      <Badge tone="warning">{Math.round(step.freezeSeconds / 60)} min Pause</Badge>
                    ) : (
                      <Badge tone="muted">{step.freezeSeconds} s Pause</Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {plan.blocked.length > 0 && (
              <Notice tone="danger" title={`${plan.blocked.length} Dienste bleiben stehen`}>
                <p>Der Server wird dadurch nicht leer und lässt sich nicht abgeben. Erst Kapazität schaffen:</p>
                <ul className="ml-4 mt-1 list-disc">
                  {plan.blocked.slice(0, 5).map((entry, index) => (
                    <li key={`${entry.label}-${index}`}>
                      {entry.label} — {entry.reason}
                    </li>
                  ))}
                  {plan.blocked.length > 5 && <li>… und {plan.blocked.length - 5} weitere</li>}
                </ul>
              </Notice>
            )}

            {plan.disruptive > 0 && (
              <Notice tone="warning" title={`${plan.disruptive} Dienste sehen eine spürbare Pause`}>
                Verträge, die über einen Backend-Pool ausgeliefert werden, ziehen unbemerkt um – der Balancer nimmt den Knoten
                aus der Rotation und die übrigen tragen weiter. Ohne Pool bleibt die Umschaltpause.
              </Notice>
            )}

            {plan.complete && (
              <label className="flex items-start gap-2 rounded-lg border divider p-3 text-xs">
                <input type="checkbox" checked={retire} onChange={(e) => setRetire(e.target.checked)} className="mt-0.5" />
                <span>
                  <strong>Server anschließend abgeben.</strong> Er nimmt ab sofort keine Neukunden mehr an und verkauft sich
                  selbst, sobald der letzte Dienst herunter ist — ohne dass jemand danebenstehen muss.
                </span>
              </label>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Dienste verlegen
// ---------------------------------------------------------------------------

/**
 * Ein wählbarer Dienst – Instanz oder Vertrag – mit allem, was für Zielauswahl
 * und Platzrechnung gebraucht wird.
 *
 * Die Ressourcen stehen hier flach nebeneinander, obwohl sie aus zwei ganz
 * verschiedenen Quellen kommen (die Instanz kennt sie selbst, der Vertrag erbt
 * sie vom Produkt). Ohne diese Vereinheitlichung müsste jede Stelle der
 * Auswahl beide Fälle erneut unterscheiden.
 */
interface MoveCandidate {
  key: string;
  kind: 'vps' | 'contract';
  id: string;
  label: string;
  sublabel: string;
  sourceServerId: ServerId;
  /** Rolle, die ein Zielserver haben muss. */
  role: Server['role'];
  cpuCores: number;
  ramGb: number;
  ssdGb: number;
  hddGb: number;
  /** Nur bei Verträgen: für die Überbuchungsrechnung von `fits`. */
  product?: Product;
}

/**
 * Prüft, ob die gesamte Auswahl auf den Zielserver passt.
 *
 * Der Server prüft jeden Umzug einzeln und reserviert den Platz sofort – aus
 * seiner Sicht ist das richtig. Für die Auswahl reicht das aber nicht: Fünf
 * Instanzen, die einzeln alle passen, passen zusammen womöglich nicht, und das
 * müsste man sonst daran merken, dass die letzten beiden Befehle scheitern,
 * nachdem die ersten drei schon laufen. Deshalb wird hier mitgezählt.
 */
function selectionFits(
  world: World,
  targetId: ServerId,
  selection: MoveCandidate[],
): { ok: true } | { ok: false; blocker: MoveCandidate; reason: string } {
  const capacity = { ...serverCapacity(world, targetId) };

  for (const entry of selection) {
    if (entry.sourceServerId === targetId) continue; // liegt schon dort, zieht nicht um

    if (entry.kind === 'vps') {
      // Für Instanzen gilt kein Überbuchungsfaktor: Zugesagter Arbeitsspeicher
      // ist zugeteilter Arbeitsspeicher. Genau so prüft es der Server auch.
      if (capacity.ramGbUsed + entry.ramGb > capacity.ramGb) {
        return { ok: false, blocker: entry, reason: `nur noch ${Math.round(capacity.ramGb - capacity.ramGbUsed)} GB Arbeitsspeicher frei` };
      }
      if (capacity.ssdGbUsed + entry.ssdGb > capacity.ssdGb) {
        return { ok: false, blocker: entry, reason: `nur noch ${Math.round(capacity.ssdGb - capacity.ssdGbUsed)} GB Speicher frei` };
      }
    } else if (entry.product && !fits(capacity, entry.product)) {
      return { ok: false, blocker: entry, reason: 'kein Platz mehr für dieses Produkt' };
    }

    capacity.cpuCoresUsed += entry.cpuCores;
    capacity.ramGbUsed += entry.ramGb;
    capacity.ssdGbUsed += entry.ssdGb;
    capacity.hddGbUsed += entry.hddGb;
  }

  return { ok: true };
}

/**
 * Verlegt einen oder mehrere Dienste auf denselben Zielserver.
 *
 * Der Dialog konnte lange nur einen Dienst auf einmal: auswählen, Ziel wählen,
 * bestätigen, Dialog schließt, von vorn. Wer eine Handvoll Instanzen von einem
 * Knoten wegräumen wollte, ohne ihn gleich ganz zu räumen, klickte das fünfmal
 * durch. Deshalb steht hier eine Liste zum Anhaken und darunter *ein* Ziel.
 *
 * Die Vorschau rechnet die Auswahl als Ganzes: Der Platz auf dem Ziel wird
 * mitgezählt, und die Dauer ist die Summe und nicht das Maximum – alle
 * Übertragungen teilen sich dieselbe Anbindung.
 */
function MoveServicesDialog({ onClose }: { onClose: () => void }) {
  const world = useGame((s) => s.world);
  const gameId = useGame((s) => s.gameId);
  const refreshWorld = useGame((s) => s.refreshWorld);
  const toast = useGame((s) => s.toast);
  const [selected, setSelected] = useState<string[]>([]);
  const [target, setTarget] = useState<ServerId | ''>('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [failures, setFailures] = useState<{ label: string; message: string }[]>([]);

  const candidates = useMemo<MoveCandidate[]>(() => {
    if (!world) return [];
    const entries: MoveCandidate[] = [];

    // Was schon unterwegs ist, steht nicht zur Auswahl – sonst wäre die halbe
    // Liste ein Angebot, das der Server anschließend ablehnt.
    const busySubjects = new Set(runningMigrations(world).map((job) => `${job.subject.kind}:${job.subject.id}`));

    for (const vps of Object.values(world.vpsInstances)) {
      const host = world.servers[vps.hostServerId];
      if (!host || busySubjects.has(`vps:${vps.id}`)) continue;
      entries.push({
        key: `vps:${vps.id}`,
        kind: 'vps',
        id: vps.id,
        label: vps.name,
        sublabel: `Instanz · ${vps.vcpu} Kerne, ${vps.ramGb} GB RAM, ${bytes(vps.diskGb)}`,
        sourceServerId: host.id,
        role: 'vps-host',
        cpuCores: vps.vcpu,
        ramGb: vps.ramGb,
        ssdGb: vps.diskGb,
        hddGb: 0,
      });
    }

    for (const contract of Object.values(world.contracts)) {
      if (contract.state !== 'active' || !contract.serverId || contract.vpsId) continue;
      if (busySubjects.has(`contract:${contract.id}`)) continue;
      const product = world.products[contract.productId];
      const source = world.servers[contract.serverId];
      if (!product || !source) continue;
      const customer = world.customers[contract.customerId];
      const role = requiredRole(product);
      entries.push({
        key: `contract:${contract.id}`,
        kind: 'contract',
        id: contract.id,
        label: `${product.name} – ${customer?.company ?? customer?.name ?? 'Kunde'}`,
        sublabel: `Vertrag · ${bytes(product.resources.ssdGb + product.resources.hddGb)}${contract.poolId ? ' · im Pool' : ''}`,
        sourceServerId: source.id,
        role: role ?? source.role,
        cpuCores: product.resources.cpuCores,
        ramGb: product.resources.ramGb,
        ssdGb: product.resources.ssdGb,
        hddGb: product.resources.hddGb,
        product,
      });
    }

    return entries.sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [world]);

  const byKey = useMemo(() => new Map(candidates.map((c) => [c.key, c])), [candidates]);
  const selection = useMemo(() => selected.map((key) => byKey.get(key)).filter((c): c is MoveCandidate => !!c), [selected, byKey]);

  /*
   * Ein Ziel muss zu *allen* gewählten Diensten passen – deshalb die
   * Schnittmenge der Rollen. Sind unterschiedliche Rollen im Spiel, bleibt die
   * Liste leer, und das ist die richtige Antwort: Eine Instanz und ein
   * Shared-Hosting-Konto haben kein gemeinsames Ziel.
   */
  const roles = useMemo(() => new Set(selection.map((c) => c.role)), [selection]);

  const targets = useMemo(() => {
    if (!world || selection.length === 0 || roles.size !== 1) return [] as Server[];
    const [role] = [...roles];
    return Object.values(world.servers)
      .filter((s) => s.role === role && s.os !== null && !s.retirement)
      .filter((s) => s.state === 'online' || s.state === 'degraded')
      // Ein Server, auf dem die gesamte Auswahl bereits liegt, ist kein Ziel.
      .filter((s) => selection.some((c) => c.sourceServerId !== s.id));
  }, [world, selection, roles]);

  // Was tatsächlich bewegt wird: Dienste, die schon auf dem Ziel liegen, nicht.
  const moving = useMemo(() => (target ? selection.filter((c) => c.sourceServerId !== target) : []), [selection, target]);
  const alreadyThere = selection.length - moving.length;

  const estimates = useMemo(() => {
    if (!world || !target) return [];
    return moving.map((entry) => ({
      entry,
      estimate:
        entry.kind === 'vps'
          ? estimateMigration(world, { kind: 'vps', id: entry.id as VpsId, targetServerId: target })
          : estimateMigration(world, { kind: 'contract', id: entry.id as ContractId, targetServerId: target }),
    }));
  }, [world, target, moving]);

  const capacityCheck = useMemo(
    () => (world && target && moving.length > 0 ? selectionFits(world, target, moving) : null),
    [world, target, moving],
  );

  if (!world) return null;

  const needle = filter.trim().toLowerCase();
  const visible = needle
    ? candidates.filter(
        (c) =>
          c.label.toLowerCase().includes(needle) ||
          (world.servers[c.sourceServerId]?.name ?? '').toLowerCase().includes(needle),
      )
    : candidates;

  // Nach Quellserver gruppiert: „alles von dieser Kiste weg“ ist der häufigste
  // Anlass für eine Mehrfachauswahl.
  const groups = new Map<ServerId, MoveCandidate[]>();
  for (const entry of visible) {
    const list = groups.get(entry.sourceServerId);
    if (list) list.push(entry);
    else groups.set(entry.sourceServerId, [entry]);
  }

  const toggle = (key: string) => {
    setFailures([]);
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleGroup = (entries: MoveCandidate[]) => {
    setFailures([]);
    const keys = entries.map((e) => e.key);
    const allSelected = keys.every((k) => selected.includes(k));
    setSelected((prev) => (allSelected ? prev.filter((k) => !keys.includes(k)) : [...new Set([...prev, ...keys])]));
  };

  const failing = estimates.filter((e) => !e.estimate.ok);
  const totalGb = estimates.reduce((sum, e) => sum + (e.estimate.ok ? e.estimate.diskGb + e.estimate.ramGb : 0), 0);
  const totalTicks = estimates.reduce((sum, e) => sum + (e.estimate.ok ? e.estimate.ticks : 0), 0);
  const disruptive = estimates.filter((e) => e.estimate.ok && !e.estimate.seamless && e.estimate.freezeSeconds >= 60).length;
  const blocked = capacityCheck && !capacityCheck.ok ? capacityCheck : null;
  const ready = moving.length > 0 && failing.length === 0 && !blocked;

  const start = async () => {
    if (!target || !gameId) return;
    setBusy(true);
    setFailures([]);
    const problems: { label: string; message: string }[] = [];
    const started: string[] = [];

    /*
     * Nacheinander, nicht nebenläufig: Jeder Umzug reserviert beim Start den
     * Platz auf dem Ziel. Parallel abgeschickt rechneten alle Befehle noch mit
     * dem leeren Server und der letzte fände nichts mehr vor.
     *
     * Und bewusst über `api.command` statt über `run` aus dem Store: Der lädt
     * nach jedem einzelnen Befehl die ganze Welt nach und wirft eine Meldung
     * aus. Bei zehn Diensten wären das zehn Weltabrufe und zehn Meldungen für
     * einen Vorgang, den der Spieler als einen versteht.
     */
    for (const entry of moving) {
      try {
        const result = await api.command(
          gameId,
          entry.kind === 'vps'
            ? { type: 'vps/migrate', vpsId: entry.id as VpsId, targetServerId: target }
            : { type: 'contract/migrate', contractId: entry.id as ContractId, targetServerId: target },
        );
        if (result.ok) started.push(entry.key);
        else problems.push({ label: entry.label, message: result.message });
      } catch (error) {
        problems.push({ label: entry.label, message: (error as Error).message });
      }
    }

    await refreshWorld();
    setBusy(false);

    if (started.length > 0) {
      toast('success', started.length === 1 ? 'Umzug eingereiht.' : `${started.length} Umzüge eingereiht.`);
    }
    if (problems.length === 0) {
      onClose();
      return;
    }
    // Was lief, verschwindet aus der Auswahl; was hakt, bleibt mit Begründung stehen.
    setSelected((prev) => prev.filter((key) => !started.includes(key)));
    setFailures(problems);
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Dienste verlegen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} disabled={!ready} onClick={() => void start()}>
            {moving.length > 1 ? `${moving.length} Umzüge starten` : 'Umzug starten'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium">
              Was soll umziehen?{' '}
              <span className="font-normal text-panel-500 dark:text-panel-400">
                {selected.length > 0 ? `${selected.length} ausgewählt` : 'mehrere möglich'}
              </span>
            </p>
            {selected.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected([]);
                  setFailures([]);
                }}
              >
                Auswahl leeren
              </Button>
            )}
          </div>

          <Input placeholder="Nach Dienst oder Server filtern…" value={filter} onChange={(e) => setFilter(e.target.value)} />

          <div className="mt-2 max-h-64 space-y-3 overflow-y-auto rounded-lg border divider p-2">
            {groups.size === 0 && <p className="p-2 text-xs text-panel-500 dark:text-panel-400">Nichts gefunden.</p>}
            {[...groups.entries()].map(([serverId, entries]) => (
              <div key={serverId}>
                <button
                  className="mb-1 flex w-full items-center justify-between gap-2 px-1 text-2xs font-medium uppercase tracking-wide text-panel-500 hover:text-panel-800 dark:text-panel-400 dark:hover:text-panel-200"
                  onClick={() => toggleGroup(entries)}
                >
                  <span>
                    {world.servers[serverId]?.name ?? 'Unbekannt'} · {world.datacenters[world.servers[serverId]?.datacenterId]?.city ?? '?'}
                  </span>
                  <span>alle</span>
                </button>
                <ul className="space-y-1">
                  {entries.map((entry) => (
                    <li key={entry.key}>
                      <label className="surface-muted flex cursor-pointer items-start gap-2 p-2 text-xs">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={selected.includes(entry.key)}
                          onChange={() => toggle(entry.key)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{entry.label}</span>
                          <span className="block text-2xs text-panel-500 dark:text-panel-400">{entry.sublabel}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {selection.length > 0 && roles.size > 1 && (
          <Notice tone="warning" title="Diese Dienste haben kein gemeinsames Ziel">
            Ausgewählt sind Dienste mit unterschiedlichen Anforderungen an die Serverrolle ({[...roles].join(', ')}). Ein Umzug
            geht immer auf einen Server, und der hat genau eine Rolle – die Auswahl muss deshalb in mehrere Durchgänge zerfallen.
          </Notice>
        )}

        {selection.length > 0 && roles.size === 1 && (
          <Select
            label="Wohin?"
            value={target}
            onChange={(e) => {
              setTarget(e.target.value as ServerId);
              setFailures([]);
            }}
            hint={targets.length === 0 ? 'Kein passender Server verfügbar – Rolle, Betriebssystem und Zustand müssen stimmen.' : undefined}
          >
            <option value="">– bitte wählen –</option>
            {targets.map((s) => {
              const capacity = serverCapacity(world, s.id);
              return (
                <option key={s.id} value={s.id}>
                  {s.name} — {pct(capacity.ramGbUsed / Math.max(1, capacity.ramGb), 0)} belegt, {world.datacenters[s.datacenterId]?.city}
                </option>
              );
            })}
          </Select>
        )}

        {alreadyThere > 0 && (
          <Notice tone="info" title={`${alreadyThere} Dienste liegen bereits dort`}>
            Sie bleiben unangetastet; gestartet werden nur die übrigen {moving.length}.
          </Notice>
        )}

        {blocked && (
          <Notice tone="warning" title="Die Auswahl passt nicht auf einen Server">
            Bei <strong>{blocked.blocker.label}</strong> ist Schluss: {blocked.reason}. Einzeln betrachtet passt jeder Dienst –
            zusammen nicht. Weniger auswählen oder ein größeres Ziel nehmen.
          </Notice>
        )}

        {failing.length > 0 && (
          <Notice tone="warning" title={`${failing.length} Dienste lassen sich so nicht verlegen`}>
            <ul className="ml-4 list-disc">
              {failing.slice(0, 5).map(({ entry, estimate }) => (
                <li key={entry.key}>
                  {entry.label} — {estimate.error}
                </li>
              ))}
              {failing.length > 5 && <li>… und {failing.length - 5} weitere</li>}
            </ul>
          </Notice>
        )}

        {failures.length > 0 && (
          <Notice tone="danger" title={`${failures.length} Umzüge wurden abgelehnt`}>
            <p>Die übrigen laufen bereits. Was hakt:</p>
            <ul className="ml-4 mt-1 list-disc">
              {failures.map((entry, index) => (
                <li key={`${entry.label}-${index}`}>
                  {entry.label} — {entry.message}
                </li>
              ))}
            </ul>
          </Notice>
        )}

        {moving.length > 0 && failing.length === 0 && (
          <div className="space-y-3 rounded-lg border divider p-3">
            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              <Field label="Umzüge">{num(moving.length)}</Field>
              <Field label="Zu übertragen">{bytes(totalGb)}</Field>
              <Field label="Dauer">{duration(totalTicks)}</Field>
              <Field label="Kunden merken es">
                <span className={disruptive > 0 ? 'text-amber-600 dark:text-amber-400' : ''}>{num(disruptive)}</span>
              </Field>
            </div>

            {moving.length > 1 && (
              <p className="text-2xs text-panel-500 dark:text-panel-400">
                Die Dauer ist die Summe, nicht das Maximum: Alle Übertragungen teilen sich die Anbindung des Ziels. Sie
                gleichzeitig zu starten macht sie nicht schneller.
              </p>
            )}

            <ul className="space-y-1">
              {estimates.map(({ entry, estimate }) => (
                <li key={entry.key} className="surface-muted flex flex-wrap items-center gap-2 p-2 text-xs">
                  <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                  <span className="text-panel-500 dark:text-panel-400">→ {world.servers[target as ServerId]?.name}</span>
                  {estimate.ok && (
                    <>
                      <span className="tabular text-panel-500 dark:text-panel-400">{bytes(estimate.diskGb + estimate.ramGb)}</span>
                      <span className="tabular text-panel-500 dark:text-panel-400">{mbps(estimate.rateMbps)}</span>
                      <span className="tabular text-panel-500 dark:text-panel-400">{duration(estimate.ticks)}</span>
                      {estimate.seamless ? (
                        <Badge tone="success">ohne Unterbrechung</Badge>
                      ) : estimate.convergenceRisk ? (
                        <Badge tone="warning">Abgleich konvergiert nicht</Badge>
                      ) : estimate.freezeSeconds >= 60 ? (
                        <Badge tone="warning">{Math.round(estimate.freezeSeconds / 60)} min Pause</Badge>
                      ) : (
                        <Badge tone="muted">{Math.round(estimate.freezeSeconds)} s Pause</Badge>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>

            {estimates.some((e) => e.estimate.ok && e.estimate.crossSite) && (
              <p className="text-2xs text-panel-500 dark:text-panel-400">
                Ein Teil läuft über den eigenen Backbone zwischen zwei Standorten.
              </p>
            )}

            {estimates.some((e) => e.estimate.ok && e.estimate.convergenceRisk) && (
              <Notice tone="warning" title="Ein Speicherabgleich wird nicht konvergieren">
                Die Instanz beschreibt ihren Arbeitsspeicher schneller, als die Leitung ihn überträgt. Das Umschalten wird nach
                einigen Durchgängen erzwungen und kostet eine spürbare Pause. Nachts, bei niedriger Last, liefe derselbe Umzug
                unbemerkt durch.
              </Notice>
            )}
          </div>
        )}

        <Notice tone="info" title="Warum Umzüge dauern">
          Übertragen wird über die Anbindung des langsameren der beiden Knoten, und davon nur die Hälfte – die andere gehört den
          Kunden. Ein 1G-Port trägt damit rund <Mono>225 GB</Mono> pro Stunde. Wer regelmäßig große Instanzen bewegt, kauft in
          Wahrheit 10G-Ports.
        </Notice>
      </div>
    </Modal>
  );
}
